var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { MEDICATION_REQUEST_STATUS_SELECT_ID, MEDICATION_TIMING_ABBREVIATION_SELECT_ID, } from 'ziphy-web-shared/basic/entities/notes/definitions';
import { useLoader } from 'ziphy-web-shared/basic/hooks';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { useMobxForm } from 'ziphy-web-shared/basic/lib/forms';
import { DateInput, Select, Textarea, ToggleButtonGroup, } from 'ziphy-web-shared/basic/lib/forms/fields';
import { useDefinitionSelectItems } from 'ziphy-web-shared/basic/lib/forms/fields/select/Select.hooks';
import { Title, ToggleButton } from 'ziphy-web-shared/basic/lib/simple';
import { Modal, ModalToolbarBtn } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { $loader } from 'ziphy-web-shared/basic/utils';
import { formatDate } from 'ziphy-web-shared/basic/utils/time';
import { getFhirRef, getResourceProperties, isEmptyCode, isEnteredInErrorStatus, } from '../';
import '../../definitions';
import SearchMedicationInput from '../lib/SearchMedicationInput';
import styles from './MedicationRequest.modal.module.scss';
const MedicationRequestModal = ({ encounterId, resourceContext, resourceId: _resourceId, searchTaxonomy, canChangeCode = false, _core, }) => {
    const { t } = useTranslation();
    const [data, setData] = useState();
    const [resourceId, setResourceId] = useState(_resourceId);
    const isEdit = useMemo(() => Boolean(_resourceId), [_resourceId]);
    const [isChangeCode, setIsChangeCode] = useState(null);
    const isDisabled = useMemo(() => Boolean(searchTaxonomy) || (isEdit && !isChangeCode), [searchTaxonomy, isEdit, isChangeCode]);
    const { service } = useMemo(() => getResourceProperties(resourceContext), [resourceContext]);
    const { isLoading } = useLoader(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!resourceId) {
            if (searchTaxonomy)
                setData({ medication: searchTaxonomy.fhir });
            return;
        }
        const response = yield service.read({
            encounterId,
            fhirRef: getFhirRef(resourceContext.resourceType, resourceId),
            elementName: resourceContext.element,
        });
        if (!response.error) {
            setData(response.prepared);
            if (isChangeCode === null) {
                setIsChangeCode(isEmptyCode((_a = response.prepared.medication) === null || _a === void 0 ? void 0 : _a.code) || canChangeCode);
            }
        }
    }), [resourceId]);
    const form = useMobxForm(useMemo(() => ({
        initialValues: data,
        fieldsConfig: {
            medication: {
                validate: [
                    'required',
                    (value, formValues) => {
                        if (isEnteredInErrorStatus(formValues.status))
                            return '';
                        return isEmptyCode(value === null || value === void 0 ? void 0 : value.code) ? t('alert.notes_specify_code') : '';
                    },
                ],
            },
            status: { validate: ['required'] },
            'dosage.timing.code': {
                validate: ['required', 'requiredCode'],
                transformIn: ['fromCodeTextToOption'],
                transformOut: ['fromOptionToCodeText'],
            },
            'dosage.instruction': { transformOut: ['emptyNull'] },
            note: { transformOut: ['emptyNull'] },
            'dispense.valid.start': { validate: ['required'] },
            // 'dispense.valid.end': { validate: ['required'] },
        },
    }), [data]));
    const medication = form.field('medication', {});
    const status = form.field('status', 'draft');
    const frequency = form.field('dosage.timing.code', {});
    const patientNote = form.field('dosage.instruction', '');
    const pharmacyNote = form.field('note', '');
    const startDate = form.field('dispense.valid.start', formatDate(new Date(), 'date'));
    const endDate = form.field('dispense.valid.end', '');
    const substitution = form.field('substitution.isAllowed', false);
    const isUpdate = resourceId || isChangeCode;
    const handleSubmit = form.submit((data) => __awaiter(void 0, void 0, void 0, function* () {
        const payload = {
            encounterId,
            elementName: resourceContext.element,
            data: data,
        };
        if (isUpdate) {
            payload.data.id = isChangeCode ? _resourceId : resourceId;
            const response = yield service.save(payload);
            if (!response.error) {
                yield _core.onHide(true, {
                    resourceId: response.prepared.id,
                    action: isEnteredInErrorStatus(data.status) ? 'DELETE' : 'UPDATE',
                });
            }
        }
        else {
            const response = yield service.create(payload);
            if (!response.error) {
                yield _core.onHide(true, {
                    resourceId: response.prepared.id,
                    action: 'CREATE',
                });
            }
        }
    }), { onlyChanged: isUpdate });
    const isEditable = status.initialValue === 'draft';
    const allStatuses = ['draft', 'active', 'cancelled'];
    const editableStatuses = ['active', 'cancelled'];
    const definitionStatusItems = useDefinitionSelectItems(MEDICATION_REQUEST_STATUS_SELECT_ID);
    const statusItems = useMemo(() => {
        if (!allStatuses.includes(status.initialValue))
            return definitionStatusItems;
        return definitionStatusItems.filter((x) => {
            if (editableStatuses.includes(status.initialValue)) {
                return editableStatuses.includes(x.id);
            }
            return allStatuses.includes(x.id);
        });
    }, [status.initialValue, definitionStatusItems]);
    return (_jsxs(Modal, Object.assign({ size: 'xl', centered: true, scrollable: true }, { children: [_jsx("div", Object.assign({ className: "modal-toolbar" }, { children: _jsx(ModalToolbarBtn, { type: "close", action: _core.onHide }) })), _jsx(Modal.Header, { children: isEdit ? t('label.prescription') : t('label.add_prescription') }), _jsx(Modal.Body, Object.assign({ isLoading: isLoading }, { children: _jsxs("form", Object.assign({ id: form.id, onSubmit: $loader.registerHandler(form.id, handleSubmit) }, { children: [_jsxs("div", Object.assign({ className: "row row--gutters-5 row--mb-10" }, { children: [_jsx("div", Object.assign({ className: "col-12" }, { children: _jsx(SearchMedicationInput, { field: medication, errorMessage: medication.error, setResourceId: setResourceId, encounterId: encounterId, resourceContext: resourceContext, 
                                        // label={t('label.medication')}
                                        placeholder: t('label.search_by_name_or_code'), mode: ['md'], disabled: isDisabled || !isEditable, clear: !isDisabled && isEditable }) })), _jsxs("div", Object.assign({ className: "col-12" }, { children: [_jsx(Title, Object.assign({ size: 'h6', className: 'mb-10' }, { children: t('label.frequency') })), _jsx(ToggleButtonGroup, { field: frequency, size: 'sm', options: useDefinitionSelectItems(MEDICATION_TIMING_ABBREVIATION_SELECT_ID, {
                                                customOther: {
                                                    id: 'other',
                                                    text: t('label.other'),
                                                    extraField: {
                                                        element: 'input',
                                                        autoFocus: true,
                                                        placeholder: t('label.any_details'),
                                                    },
                                                },
                                            }), renderOption: { checkbox: { position: 'none' } }, disabled: !isEditable })] })), _jsx("div", Object.assign({ className: "col-12" }, { children: _jsx(Textarea, { field: patientNote, mode: ['sm'], label: t('label.patient_note'), rows: 1, disabled: !isEditable }) }))] })), _jsxs("div", Object.assign({ className: classNames('row row--gutters-5 row--mb-10 mt-20', styles.prescription) }, { children: [_jsx("div", Object.assign({ className: "col-12 col-md-4" }, { children: _jsx(DateInput, { field: startDate, maxDate: 'today', label: t('label.effective_date'), mode: ['sm'], disabled: !isEditable }) })), _jsx("div", Object.assign({ className: "col-12 col-md-4" }, { children: _jsx(DateInput, { field: endDate, minDate: 1, label: t('label.discard_after'), mode: ['sm'], disabled: !isEditable }) })), _jsx("div", Object.assign({ className: "col-12 col-md-4" }, { children: _jsx(Select, { field: status, mode: ['sm'], items: statusItems, label: t('label.status'), placeholder: t('label.select_status'), disabled: !isEdit }) })), _jsx("div", Object.assign({ className: "col-12" }, { children: _jsx(Textarea, { field: pharmacyNote, mode: ['sm'], label: t('label.pharmacy_note'), rows: 1, disabled: !isEditable }) })), _jsx("div", Object.assign({ className: "col-12" }, { children: _jsx(ToggleButton, { mode: ['simple', 'sm'], selected: !substitution.value, onClick: () => substitution.onChange(!substitution.value), invalid: substitution.isInvalid, label: t('label.no_substitutions'), checkbox: { mode: 'circle' }, disabled: !isEditable }) }))] }))] })) })), _jsxs(Modal.Footer, Object.assign({ className: 'gap-10' }, { children: [_jsx(Button, Object.assign({ mode: ['primary', 'sm', 'min110'], form: form.id, isLoading: $loader.isRunHandler(form.id), disabled: isLoading }, { children: t('btn.save') })), _jsx(Button, Object.assign({ mode: ['secondary', 'sm', 'min110'], disabled: isLoading, onClick: () => _core.onHide() }, { children: t('btn.cancel') }))] }))] })));
};
MedicationRequestModal.displayName = 'MedicationRequestModal';
export default observer(MedicationRequestModal);
